import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Film, Plus, Search, Trash2, X, Clock, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { contentApi } from '@/api/client';
import type { ContentItem, ContentRating } from '@/types';

// ---- Rating Badge ----

function RatingBadge({ rating }: { rating: ContentRating }) {
  const colors: Record<ContentRating, string> = {
    G: 'bg-status-healthy/20 text-status-healthy',
    PG: 'bg-status-healthy/20 text-status-healthy',
    'PG-13': 'bg-status-warning/20 text-status-warning',
    R: 'bg-status-error/20 text-status-error',
    NR: 'bg-silver-muted/20 text-silver-muted',
  };

  return (
    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold', colors[rating])}>
      {rating}
    </span>
  );
}

// ---- Duration Formatter ----

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// ---- Content Card ----

function ContentCard({
  item,
  onEdit,
  onDelete,
}: {
  item: ContentItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      onClick={onEdit}
      className="bg-surface border border-surface-elevated rounded-xl p-5 cursor-pointer hover:border-gold/30 transition-colors group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-surface-elevated flex items-center justify-center">
            <Film size={18} className="text-gold" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-silver-bright group-hover:text-gold-light transition-colors truncate">
              {item.title}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-silver-muted flex items-center gap-1">
                <Clock size={10} />
                {formatDuration(item.durationMs)}
              </span>
              <RatingBadge rating={item.rating} />
            </div>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="text-silver-muted hover:text-status-error transition-colors opacity-0 group-hover:opacity-100"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {item.categories.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {item.categories.map((cat) => (
            <span
              key={cat}
              className="px-2 py-0.5 rounded-full bg-gold/10 text-gold text-[10px] font-medium"
            >
              {cat}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-silver-muted">
        <span className="flex items-center gap-1">
          <BarChart3 size={10} />
          {item.playCount} plays
        </span>
        <span>{item.language.toUpperCase()}</span>
      </div>
    </div>
  );
}

// ---- Content Dialog ----

interface ContentDialogProps {
  open: boolean;
  item: ContentItem | null;
  onClose: () => void;
}

function ContentDialog({ open, item, onClose }: ContentDialogProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [durationMin, setDurationMin] = useState(item ? String(Math.floor(item.durationMs / 60000)) : '');
  const [durationSec, setDurationSec] = useState(item ? String(Math.floor((item.durationMs % 60000) / 1000)) : '');
  const [contentUrl, setContentUrl] = useState(item?.contentUrl ?? '');
  const [thumbnailUrl, setThumbnailUrl] = useState(item?.thumbnailUrl ?? '');
  const [categories, setCategories] = useState(item?.categories.join(', ') ?? '');
  const [rating, setRating] = useState<ContentRating>(item?.rating ?? 'NR');
  const [language, setLanguage] = useState(item?.language ?? 'en');
  const [adBreakPoints, setAdBreakPoints] = useState(
    item?.adBreakPoints.map((p) => String(Math.floor(p / 1000))).join(', ') ?? '',
  );

  const createMutation = useMutation({
    mutationFn: () => {
      const durationMs = (Number(durationMin) * 60 + Number(durationSec)) * 1000;
      const cats = categories
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      const breakPts = adBreakPoints
        .split(',')
        .map((p) => Number(p.trim()) * 1000)
        .filter((n) => !isNaN(n) && n > 0);

      const data = {
        title,
        description,
        durationMs,
        categories: cats,
        contentUrl,
        thumbnailUrl,
        adBreakPoints: breakPts,
        rating,
        language,
      };

      if (item) {
        return contentApi.update(item.id, data);
      }
      return contentApi.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['content-categories'] });
      onClose();
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-lg font-semibold text-silver-bright">
            {item ? 'Edit Content' : 'Add Content'}
          </h3>
          <button onClick={onClose} className="text-silver-muted hover:text-silver-bright">
            <X size={18} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="p-6 space-y-4"
        >
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Title *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="Content title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50 resize-none"
              placeholder="Content description"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-silver-muted mb-1.5">Duration (min) *</label>
              <input
                type="number"
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                required
                min={0}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
                placeholder="30"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-silver-muted mb-1.5">Duration (sec)</label>
              <input
                type="number"
                value={durationSec}
                onChange={(e) => setDurationSec(e.target.value)}
                min={0}
                max={59}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
                placeholder="0"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Content URL</label>
            <input
              value={contentUrl}
              onChange={(e) => setContentUrl(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50 font-mono"
              placeholder="https://storage.example.com/content.mp4"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Thumbnail URL</label>
            <input
              value={thumbnailUrl}
              onChange={(e) => setThumbnailUrl(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50 font-mono"
              placeholder="https://storage.example.com/thumb.jpg"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Categories</label>
            <input
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="comedy, drama, action (comma-separated)"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-silver-muted mb-1.5">Rating</label>
              <select
                value={rating}
                onChange={(e) => setRating(e.target.value as ContentRating)}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
              >
                <option value="NR">NR</option>
                <option value="G">G</option>
                <option value="PG">PG</option>
                <option value="PG-13">PG-13</option>
                <option value="R">R</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-silver-muted mb-1.5">Language</label>
              <input
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
                placeholder="en"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">
              Ad Break Points (seconds, comma-separated)
            </label>
            <input
              value={adBreakPoints}
              onChange={(e) => setAdBreakPoints(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="300, 900, 1500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title || !durationMin || createMutation.isPending}
              className="px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isPending ? 'Saving...' : item ? 'Save Changes' : 'Add Content'}
            </button>
          </div>
          {createMutation.isError && (
            <p className="text-xs text-status-error">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to save content'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

// ---- Main Page ----

export default function VOD() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<ContentItem | null>(null);

  const { data: content, isLoading } = useQuery({
    queryKey: ['content', searchQuery, categoryFilter],
    queryFn: () =>
      contentApi.list({
        search: searchQuery || undefined,
        category: categoryFilter || undefined,
      }),
    refetchInterval: 10_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['content-categories'],
    queryFn: contentApi.getCategories,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => contentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['content-categories'] });
    },
  });

  function openCreate() {
    setEditItem(null);
    setDialogOpen(true);
  }

  function openEdit(item: ContentItem) {
    setEditItem(item);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditItem(null);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-silver-bright">VOD Library</h2>
          <p className="text-sm text-silver-muted mt-1">
            Manage video-on-demand assets for FAST channel scheduling.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light transition-colors"
        >
          <Plus size={16} />
          Add Content
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-silver-muted" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-surface border border-surface-elevated rounded-lg pl-9 pr-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
            placeholder="Search content..."
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-silver-muted hover:text-silver-bright"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-surface border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
        >
          <option value="">All Categories</option>
          {categories?.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {isLoading && (
        <div className="text-center py-12 text-silver-muted text-sm">Loading content...</div>
      )}

      {content && content.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <Film size={40} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted">No content yet.</p>
          <p className="text-silver-muted/70 text-sm max-w-md mx-auto">
            Add VOD assets to use with FAST channel scheduling.
          </p>
          <button
            onClick={openCreate}
            className="text-gold text-sm hover:text-gold-light transition-colors"
          >
            Add your first content
          </button>
        </div>
      )}

      {content && content.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {content.map((item) => (
            <ContentCard
              key={item.id}
              item={item}
              onEdit={() => openEdit(item)}
              onDelete={() => deleteMutation.mutate(item.id)}
            />
          ))}
        </div>
      )}

      <ContentDialog open={dialogOpen} item={editItem} onClose={closeDialog} />
    </div>
  );
}
